// Width of the settings sidebar (min-w-[15.1rem]) and of the right rail
// (w-[33.5rem]) that pages like the agent and widget settings mount beside the
// content, in px. The back link may only float once the gutter left of the
// content column clears it.
const SETTINGS_SIDEBAR_WIDTH = 15.1 * 16
const SETTINGS_RIGHT_RAIL_WIDTH = 33.5 * 16

// Chevron, gap, button padding, and the longest parent nav title.
const WIDEST_BACK_LINK = 160

// A full-width page has no cap, so it leaves no gutter for the back link.
export const SETTINGS_COLUMN_WIDTHS = {
	default: 672,
	wide: 896,
	extraWide: 1180,
	full: Number.POSITIVE_INFINITY,
} as const

export type SettingsColumn = keyof typeof SETTINGS_COLUMN_WIDTHS

const FLOATING_BASE =
	"absolute left-0 top-0 z-10 hidden px-2 pt-2 sm:px-2.5 sm:pt-2.5"
// In flow so it can never cover the heading, sticky so the page scrolls under it.
const INLINE_BASE =
	"sticky top-0 z-10 block bg-gray-100 px-2 pt-2 sm:px-8 sm:pt-4 dark:bg-gray-950"

// Tailwind needs every arbitrary variant spelled out, so the tiers are literal.
export const FLOATING_BACK_LINK_PLACEMENTS = [
	{
		breakpoint: 1240,
		floating: `${FLOATING_BASE} min-[1240px]:block`,
		inline: `${INLINE_BASE} min-[1240px]:hidden`,
	},
	{
		breakpoint: 1500,
		floating: `${FLOATING_BASE} min-[1500px]:block`,
		inline: `${INLINE_BASE} min-[1500px]:hidden`,
	},
	{
		breakpoint: 1800,
		floating: `${FLOATING_BASE} min-[1800px]:block`,
		inline: `${INLINE_BASE} min-[1800px]:hidden`,
	},
] as const

const NEVER_FLOATS = { floating: FLOATING_BASE, inline: INLINE_BASE }

// Keeps the link readable wherever it floats: the page colour stays solid under
// the label and fades out before it meets the content.
export const FLOATING_BACK_LINK_SCRIM_CLASS =
	"pointer-events-none absolute inset-y-0 left-0 right-[-3rem] bg-gradient-to-r from-gray-100 from-70% to-transparent dark:from-gray-950"

// Hangs below the sticky bar: the page colour and a blur that both fade out, so
// content scrolling under the link dissolves instead of hitting a hard edge.
export const INLINE_BACK_LINK_FADE_CLASS =
	"pointer-events-none absolute inset-x-0 top-full h-8 bg-gradient-to-b from-gray-100 to-transparent backdrop-blur-sm [mask-image:linear-gradient(to_bottom,black,transparent)] dark:from-gray-950"

export function settingsGutterWidth(
	viewportWidth: number,
	{
		column = "default",
		hasRightRail = false,
	}: { column?: SettingsColumn; hasRightRail?: boolean } = {},
): number {
	const contentWidth =
		viewportWidth -
		SETTINGS_SIDEBAR_WIDTH -
		(hasRightRail ? SETTINGS_RIGHT_RAIL_WIDTH : 0)
	return Math.max(0, (contentWidth - SETTINGS_COLUMN_WIDTHS[column]) / 2)
}

export function settingsBackLinkPlacement(options: {
	column?: SettingsColumn
	hasRightRail?: boolean
}): { floating: string; inline: string } {
	const tier = FLOATING_BACK_LINK_PLACEMENTS.find(
		({ breakpoint }) =>
			settingsGutterWidth(breakpoint, options) > WIDEST_BACK_LINK,
	)
	return tier ?? NEVER_FLOATS
}
